import { useState } from "react";
import { ChevronDown, Info } from "lucide-react";

/**
 * Public disclosure of how TruWe collects reviews.
 *
 * The 50 TruCoins reward is an engagement incentive, not a payment for praise:
 * it is credited for every published review regardless of star rating, and
 * reviews are only ever removed for spam, abuse or fake content. Disclosing
 * that plainly is both an honesty signal and an endorsement-disclosure
 * requirement (ASCI / FTC).
 */
export function ReviewDisclosure({ compact = false }: { compact?: boolean }) {
  const [open, setOpen] = useState(false);

  const points = [
    "Invite-only: we ask for a review after your order is delivered, nobody else can post.",
    "Every published review earns 50 TruCoins, whatever the rating. A 1-star review earns exactly the same as a 5-star one.",
    "We publish critical reviews. We never remove a review for being negative.",
    "We only reject spam, abuse, off-topic content or reviews we can't match to a real order.",
    "We never edit the wording of a review, and we never ask for a particular rating.",
    "Reviews tagged \u201cBeta tester\u201d come from people who received product free before launch. They were not paid, and their ratings are kept out of the star rating shown in search results.",
  ];

  if (compact) {
    return (
      <p className="text-xs leading-relaxed text-[#2C2C2C]/60">
        Reviews are invite-only after delivery. Every published review earns 50 TruCoins regardless of
        rating, we reward honest feedback, not good ratings.
      </p>
    );
  }

  return (
    <div className="rounded-2xl border border-[#D9D9D6] bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm font-medium text-[#2C2C2C]"
      >
        <Info size={15} className="shrink-0 text-[#1E5EFF]" />
        How we collect reviews
        <span className="ml-auto flex items-center gap-2 text-xs font-normal text-[#2C2C2C]/60">
          Incentive disclosed
          <ChevronDown size={15} className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </span>
      </button>
      {open && (
        <ul className="space-y-2 border-t border-[#D9D9D6] px-4 py-3 text-sm leading-relaxed text-[#2C2C2C]/75">
          {points.map((p) => (
            <li key={p} className="flex gap-2">
              <span aria-hidden className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-[#1E5EFF]" />
              <span>{p}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
